import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ApiClient, DeskSocket } from '@desk/client';
import { LIMITS, VISITOR_NAMESPACE } from '@desk/contracts/constants';

/**
 * The live call's id, kept per tab so a reload can reattach to it.
 *
 * sessionStorage rather than localStorage: it survives a reload of this tab and
 * nothing else, so a second tab opened separately does not also pick the call
 * up and start a second microphone sending the same speech twice.
 */
const CALL_KEY = 'desk.callId';

function rememberCall(callId) {
  try {
    if (callId) sessionStorage.setItem(CALL_KEY, callId);
    else sessionStorage.removeItem(CALL_KEY);
  } catch {
    /* Storage refused: a reload simply loses the call panel, as it used to. */
  }
}

function rememberedCall() {
  try {
    return sessionStorage.getItem(CALL_KEY);
  } catch {
    return null;
  }
}

/**
 * A typed chat message, as a line in the call transcript.
 *
 * During a call the chat list is replaced by the call panel, so a line typed on
 * either side would otherwise arrive where nobody is looking. The shape matches
 * a spoken segment's, which is what lets the panel speak an agent's typed reply
 * exactly as it speaks what they say.
 */
function typedLine(m) {
  const own = m.sender === 'visitor';
  return {
    segId: `msg-${m.id}`,
    own,
    typed: true,
    speaker: own ? 'you' : 'agent',
    text: own ? m.content : (m.translated ?? m.content),
    original: own ? m.translated : m.translated ? m.content : null,
    lang: own ? m.sourceLang : m.translated ? (m.targetLang ?? m.sourceLang) : m.sourceLang,
    originalLang: own ? m.targetLang : m.sourceLang,
    // An untranslated reply is shown and never spoken, the same rule as speech.
    translated: !m.translationFailed,
    arrivedAt: Date.now(),
    at: m.at,
  };
}

/**
 * All of the widget's state, in one reducer.
 *
 * The important rule is in the call transitions: the widget never moves itself
 * from `queued` to `active`. It emits an intent, and the state changes when the
 * server says so. That is why reloading the page, opening a second tab or
 * losing the network for ten seconds cannot leave the two sides disagreeing
 * about whether a call is live.
 */

const INITIAL = {
  phase: 'loading',
  error: null,
  config: null,
  /** The primary language: what agent replies arrive in. */
  language: 'en',
  /** Every language the visitor selected, primary first. Never empty. */
  languages: ['en'],
  messages: [],
  leadCaptured: false,
  leadPromptDismissed: false,
  connected: false,
  sending: false,
  agentTyping: null,

  callId: null,
  callStatus: null,
  callGrant: null,
  queuePosition: null,
  translation: 'ok',
  translationMessage: '',
  subtitles: [],
};

function reducer(state, action) {
  switch (action.type) {
    case 'loaded':
      return {
        ...state,
        phase: 'ready',
        config: action.config,
        language: action.language,
        languages: action.languages,
      };
    case 'error':
      return { ...state, phase: 'error', error: action.message };
    case 'history':
      return {
        ...state,
        messages: action.history.messages,
        leadCaptured: action.history.leadCaptured,
        // The server's copy wins on reconnect. It is the one that was stored,
        // and it is what the agent's replies are already being aimed at.
        language: action.history.language,
        languages: action.history.languages ?? [action.history.language],
      };
    case 'message': {
      // The socket may echo a message this tab already appended optimistically.
      if (state.messages.some((m) => m.id === action.message.id)) return state;
      const next = { ...state, messages: [...state.messages, action.message], agentTyping: null };
      // Mid-call, the line also goes into the call panel — the only thing on
      // screen — and an agent's typed reply is spoken there like speech.
      if (!state.callId || action.message.spoken) return next;
      const line = typedLine(action.message);
      if (state.subtitles.some((s) => s.segId === line.segId)) return next;
      return { ...next, subtitles: [...state.subtitles.slice(-40), line] };
    }
    case 'sending':
      return { ...state, sending: action.value };
    case 'connected':
      return { ...state, connected: action.value };
    case 'language':
      return { ...state, language: action.language, languages: action.languages };
    case 'lead':
      return { ...state, leadCaptured: action.captured };
    case 'dismissLead':
      return { ...state, leadPromptDismissed: true };
    case 'typing':
      return { ...state, agentTyping: action.agentName };
    case 'callQueued':
      return {
        ...state,
        callId: action.callId,
        callStatus: action.status,
        queuePosition: action.position,
        subtitles: [],
        translation: 'ok',
        translationMessage: '',
      };
    case 'callConnect':
      return {
        ...state,
        callId: action.callId,
        callGrant: action.grant,
        callStatus: 'active',
        queuePosition: null,
      };
    case 'callStatus':
      return { ...state, callStatus: action.status };
    case 'callEnded':
      return {
        ...state,
        callId: null,
        callStatus: null,
        callGrant: null,
        queuePosition: null,
      };
    case 'queuePosition':
      return { ...state, queuePosition: action.position };
    case 'translation':
      return { ...state, translation: action.state, translationMessage: action.message };
    case 'subtitle': {
      const seg = action.segment;
      // v2a is this visitor's own speech, recognised and translated for the
      // agent. Showing both directions is what makes a mistranslation visible
      // to the person best placed to correct it.
      const own = seg.direction === 'v2a';
      /*
       * Both renditions are kept, not one.
       *
       * `text` is what this visitor reads — their own words when they spoke,
       * the translation into their language when the agent did. `original` is
       * the other side of the same segment, shown small underneath: for an
       * agent line that is the English the agent actually said, which is what
       * makes a mistranslation visible instead of invisible.
       */
      const line = {
        segId: seg.segId,
        own,
        speaker: own ? 'you' : 'agent',
        text: own ? seg.sourceText : seg.targetText,
        original: own ? seg.targetText : seg.sourceText,
        lang: own ? seg.sourceLang : seg.targetLang,
        originalLang: own ? seg.targetLang : seg.sourceLang,
        // False only when every provider failed and the server passed the
        // original through. Shown, never spoken — see the contract note.
        translated: seg.translated !== false,
        // Server-side translation cost and who answered, carried so the client
        // can print a full latency breakdown rather than guessing at it.
        mtMs: seg.stages?.mtMs,
        provider: seg.stages?.provider,
        arrivedAt: Date.now(),
        at: seg.at ?? new Date().toISOString(),
      };
      if (state.subtitles.some((s) => s.segId === line.segId)) return state;
      return { ...state, subtitles: [...state.subtitles.slice(-40), line] };
    }
    default:
      return state;
  }
}

export function useDesk(opts) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const socketRef = useRef(null);
  /** Held in state as well, so the call panel re-renders once it exists. */
  const [socket, setSocket] = useState(null);
  /** The current call, readable from socket handlers without re-subscribing. */
  const callIdRef = useRef(null);
  callIdRef.current = state.callId;

  const api = useMemo(() => new ApiClient({ baseUrl: `${opts.baseUrl}/api/v1` }), [opts.baseUrl]);

  /* ---------------------------------------------------------- bootstrap */

  useEffect(() => {
    let cancelled = false;

    /**
     * Reattaches to a call this tab was on before a reload.
     *
     * A reload drops the call panel but not the call: the server still has it
     * live and the agent's console still shows it. Only a fresh page does this
     * — a reconnect in the middle of a call already has its call and its
     * microphone, and must not be handed a second grant that restarts them.
     */
    const rejoinCall = async (sock) => {
      const callId = rememberedCall();
      if (!callId || callIdRef.current) return;
      try {
        const res = await sock.emitWithAck('call:rejoin', { callId });
        if (cancelled) return;
        if (res.status === 'queued' || res.status === 'ringing') {
          dispatch({
            type: 'callQueued',
            callId,
            status: res.status,
            position: res.queuePosition ?? null,
          });
        } else if (res.status === 'ended' || res.status === 'missed') {
          rememberCall(null);
        }
        // connecting / active: the server has already sent `call:connect` with
        // a fresh grant, and that rebuilds the call panel on its own.
      } catch {
        // Not this visitor's call any more, or it no longer exists.
        rememberCall(null);
      }
    };

    const connect = () => {
      if (socketRef.current) return;

      const sock = new DeskSocket({
        url: opts.socketUrl,
        namespace: VISITOR_NAMESPACE,
        onConnect: () => {
          dispatch({ type: 'connected', value: true });
          sock
            .emitWithAck('chat:history')
            .then((history) => dispatch({ type: 'history', history }))
            .catch(() => {});
          void rejoinCall(sock);
        },
        onDisconnect: () => dispatch({ type: 'connected', value: false }),
      });

      sock.on('chat:message', (p) => dispatch({ type: 'message', message: p.message }));
      sock.on('chat:typing', (p) => dispatch({ type: 'typing', agentName: p.agentName }));
      sock.on('call:connect', (p) => {
        rememberCall(p.callId);
        dispatch({ type: 'callConnect', callId: p.callId, grant: p.grant });
      });
      sock.on('call:queue', (p) => dispatch({ type: 'queuePosition', position: p.position }));
      sock.on('call:ended', () => {
        rememberCall(null);
        dispatch({ type: 'callEnded' });
      });
      sock.on('call:missed', () => {
        rememberCall(null);
        dispatch({ type: 'callEnded' });
      });
      sock.on('call:segment', (p) => dispatch({ type: 'subtitle', segment: p.segment }));
      sock.on('xlate:health', (p) =>
        dispatch({ type: 'translation', state: p.state, message: p.message }),
      );
      sock.on('session:invalid', () =>
        dispatch({ type: 'error', message: 'This chat session has expired. Please reload.' }),
      );

      socketRef.current = sock;
      setSocket(sock);
    };

    (async () => {
      try {
        const query = opts.orgSlug ? `?org=${encodeURIComponent(opts.orgSlug)}` : '';
        const config = await api.get(`/widget/config${query}`);

        // Identity is issued by the server here and returned as an HttpOnly
        // cookie. The widget never sees or generates its own id — in the
        // reference system the browser invents one, and the reviewer proved
        // that anyone knowing an id could read that visitor's history.
        // The browser's locale is only a first guess for a visitor who has
        // never been here. `hello` returns the stored selection for one who
        // has, so a reload cannot reset a choice they made deliberately.
        const hello = await api.post('/widget/hello', {
          orgSlug: opts.orgSlug,
          language: navigator.language.split('-')[0] || config.defaultLanguage,
        });

        if (cancelled) return;
        dispatch({
          type: 'loaded',
          config,
          language: hello.language,
          languages: hello.languages ?? [hello.language],
        });
        connect();
      } catch (err) {
        if (!cancelled) {
          dispatch({
            type: 'error',
            message: err instanceof Error ? err.message : 'Could not reach support',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      socketRef.current?.close();
      socketRef.current = null;
      setSocket(null);
    };
  }, [api, opts.orgSlug, opts.socketUrl]);

  /* ------------------------------------------------------------ actions */

  const send = useCallback(async (text) => {
    const sock = socketRef.current;
    if (!sock || !text.trim()) return;
    if (text.length > LIMITS.messageChars) return;

    dispatch({ type: 'sending', value: true });
    try {
      const res = await sock.emitWithAck('chat:send', { text });
      dispatch({ type: 'message', message: res.message });
    } finally {
      dispatch({ type: 'sending', value: false });
    }
  }, []);

  /**
   * Change the language selection.
   *
   * The whole ordered set goes up and the server's normalised answer comes
   * back — the first entry is the primary. Applying the server's reply rather
   * than the optimistic local list is what keeps the widget and the desk from
   * disagreeing about which language replies are being written in.
   */
  const setLanguages = useCallback(async (languages) => {
    const sock = socketRef.current;
    if (!sock || languages.length === 0) return;
    const res = await sock.emitWithAck('chat:lang', {
      language: languages[0],
      languages,
    });
    dispatch({ type: 'language', language: res.language, languages: res.languages });
  }, []);

  /** Promote one already-selected language to primary. */
  const setLanguage = useCallback(
    (language) => setLanguages([language, ...state.languages.filter((c) => c !== language)]),
    [setLanguages, state.languages],
  );

  const submitLead = useCallback(async (lead) => {
    const sock = socketRef.current;
    if (!sock) return;
    await sock.emitWithAck('lead:submit', lead);
    dispatch({ type: 'lead', captured: true });
  }, []);

  const dismissLead = useCallback(() => dispatch({ type: 'dismissLead' }), []);

  const requestCall = useCallback(
    async ({ recordingConsent }) => {
      const sock = socketRef.current;
      if (!sock) throw new Error('Not connected');

      const ack = await sock.emitWithAck('call:request', {
        language: state.language,
        translate: true,
        agentLanguage: 'en',
        recordingConsent,
      });

      rememberCall(ack.callId);
      dispatch({
        type: 'callQueued',
        callId: ack.callId,
        status: ack.status,
        position: ack.queuePosition,
      });
      return ack;
    },
    [state.language],
  );

  const hangUp = useCallback(async () => {
    const sock = socketRef.current;
    if (!sock || !state.callId) return;
    await sock.emitWithAck('call:hangup', {
      callId: state.callId,
      reason: state.callStatus === 'active' ? 'visitor_hangup' : 'visitor_cancelled',
    });
    rememberCall(null);
    dispatch({ type: 'callEnded' });
  }, [state.callId, state.callStatus]);

  /**
   * Whether to offer the lead form.
   *
   * After a few messages, once, and never while a call is in progress —
   * interrupting someone mid-call to ask for their phone number is the kind of
   * detail that makes a widget feel automated rather than helpful.
   */
  const shouldPromptLead =
    state.phase === 'ready' &&
    !state.leadCaptured &&
    !state.leadPromptDismissed &&
    !state.callId &&
    state.messages.filter((m) => m.sender === 'visitor').length >=
      (state.config?.leadPromptAfterMessages ?? LIMITS.leadPromptAfterMessages);

  /** BCP-47 tags for speech, from the server's language table. */
  const bcp47 = useCallback(
    (code) => state.config?.languages.find((l) => l.code === code)?.bcp47 ?? code,
    [state.config],
  );

  /** A language's own name, for labelling the renditions under a reply. */
  const languageName = useCallback(
    (code) =>
      state.config?.languages.find((l) => l.code === code)?.name ?? code.toUpperCase(),
    [state.config],
  );

  return {
    state,
    socket,
    shouldPromptLead,
    bcp47,
    send,
    setLanguage,
    setLanguages,
    languageName,
    submitLead,
    dismissLead,
    requestCall,
    hangUp,
  };
}
